// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useEffect, useMemo, useState } from 'react';
import { tagsApi } from '../api/tagsApi';
import { useTags } from '../contexts/TagsContext';
import type { TagDTO, TaggableEntityType } from '../types/tag';

/**
 * Batch-decorates a page of rows with their assigned tags in ONE network
 * call, keyed by `(entityType, ids)` (Tags PRD §6.2/§8.2 — "never per-row",
 * mirroring the cross-cloud-DB-latency rule already enforced on every other
 * list-page decoration in this codebase). Re-fetches whenever the id list
 * (by value, not by reference) or the entity type changes.
 */
export function useEntityTags(
  entityType: TaggableEntityType,
  entityIds: string[],
): {
  tagsByEntityId: Record<string, TagDTO[]>;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
} {
  const { featureEnabled } = useTags();
  const [tagsByEntityId, setTagsByEntityId] = useState<Record<string, TagDTO[]>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Stable key so the effect only re-runs when the actual id set changes,
  // not when the caller passes a fresh array literal each render.
  const idsKey = useMemo(() => [...entityIds].sort().join(','), [entityIds]);

  const load = useCallback(async () => {
    if (!featureEnabled || entityIds.length === 0) {
      setTagsByEntityId({});
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await tagsApi.getForEntities(entityType, entityIds);
      setTagsByEntityId(data);
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status !== 404) {
        console.error('Failed to load entity tags:', err);
        setError(err instanceof Error ? err.message : 'Failed to load tags');
      }
      setTagsByEntityId({});
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, idsKey, featureEnabled]);

  useEffect(() => {
    load();
  }, [load]);

  return { tagsByEntityId, loading, error, refetch: load };
}
